<template>
  
  <v-card variant="tonal">
    <template v-slot:text>

      <h3 class="on-background mb-2">{{ title }} </h3>

      <p>

        <!-- current value -->
        <template v-if="current === null || current === undefined">—</template>
        <template v-if="current !== null && current !== undefined">
          {{ formatters[format](current) }}
        </template>

        <!-- previous value -->
        <span v-if="delta !== null" :class="deltaClass">
          ({{ formattedDelta }})
        </span>

      </p>

    </template>
  </v-card>

</template>

<script setup lang="ts">

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  title: string
  format?: 'none' | 'integer' | 'decimal' | 'currency' | 'percentage' | 'duration_hours' | 'duration_days'
  precision?: number
  current: undefined | null | number
  previous: undefined | null | number
  goodDelta?: 'higher' | 'lower' | 'none'
}

const props = withDefaults(defineProps<Props>(), {
  format: 'none',
  precision: 2,
  goodDelta: 'higher',
})

const { n } = useI18n()

type Formatter = (value: number) => string

const formatters: {[key: string]: Formatter} = {
  none: (value: number) => value.toString(),
  integer: (value: number) => n(value, {style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0}),
  decimal: (value: number) => n(value, {style: 'decimal', maximumFractionDigits: props.precision}),
  currency: (value: number) => n(value, 'currency'),
  percentage: (value: number) => n(value / 100, {style: 'percent', maximumFractionDigits: props.precision}),
  duration_hours: (value: number) => `${n(value, {style: 'decimal', maximumFractionDigits: props.precision})} hr`,
  duration_days: (value: number) => `${n(value, {style: 'decimal', maximumFractionDigits: props.precision})} days`,
}

const delta = computed(() => {
  if (
    props.current === null || props.current === undefined ||
    props.previous === null || props.previous === undefined
  ) {
    return null
  }
  return props.current - props.previous
})

const formattedDelta = computed(() => {

  if (delta.value === null) {
    return null
  }

  let sign = ''

  if (delta.value > 0) {
    sign = '+'
  } else if (delta.value < 0) {
    sign = '−'
  }
  
  return `${sign}${formatters[props.format](Math.abs(delta.value))}`

})

const deltaClass = computed(() => {
  
  if (delta.value === null) {
    return null
  }

  if (delta.value === 0 || props.goodDelta === 'none') {
    return 'text-grey'
  }

  switch (props.goodDelta) {
    case 'higher':
      return delta.value > 0 ? 'text-green' : 'text-red'
      break
    case 'lower':
      return delta.value < 0 ? 'text-green' : 'text-red'
      break
  }

  return 'huh?'

})

</script>
